<template>
  <div class="p-0">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold">Videos Solicitados</h1>
      <button
        @click="openNewRequestModal"
        class="px-4 py-2 text-gray-200 bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:bg-indigo-700"
      >
        Nueva solicitud
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Loading />
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
      <p>Error al cargar los videos solicitados: {{ error }}</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="videoRequests.length === 0" class="bg-gray-100 p-6 rounded-md shadow-md">
      <div class="text-center">
        <p class="text-gray-600 text-lg">No hay videos solicitados</p>
        <p class="text-gray-500 mt-2">Aquí se mostrarán los videos solicitados por los estudiantes</p>
      </div>
    </div>

    <!-- Desktop Table View (hidden on mobile) -->
    <div v-else class="hidden md:block py-2 -my-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      <div class="inline-block min-w-full overflow-hidden align-middle border-b border-gray-200 shadow sm:rounded-lg">
        <table class="min-w-full">
          <thead>
            <tr>
              <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                Título
              </th>
              <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                Descripción
              </th>
              <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                Estado
              </th>
              <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                Fecha y Hora de Solicitud
              </th>
              <th class="px-6 py-3 text-xs font-solid leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                Fecha y Hora de Actualización
              </th>
            </tr>
          </thead>
          <tbody class="bg-white">
            <tr v-for="(request, index) in videoRequests" :key="index" class="hover:bg-gray-200">
              <td class="px-6 py-4 border-b border-gray-200 whitespace-nowrap">
                <div class="ml-2">
                  <div
                    @click="openVideoModal(request)"
                    class="text-sm font-medium leading-5 text-blue-600 hover:text-blue-800 cursor-pointer underline"
                  >
                    {{ request.title }}
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 border-b border-gray-200">
                <div class="ml-2">
                  <div class="text-sm leading-5 text-gray-900 max-w-xs truncate">
                    {{ request.description }}
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 border-b border-gray-200 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="ml-2">
                    <select v-if="isAdmin" :value="request.status" @change="handleStatusChange($event, request)" class="border border-gray-300 rounded px-2 py-1 text-xs">
                      <option v-for="status in statuses" :value="status" :key="status">{{ getStatusLabel(status) }}</option>
                    </select>
                    <span v-else :class="getStatusClass(request.status)" class="inline-flex px-2 py-1 text-xs font-semibold rounded-full">
                      {{ getStatusLabel(request.status) }}
                    </span>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 border-b border-gray-200 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="ml-2">
                    <div class="text-sm font-medium leading-5 text-gray-900">
                      {{ formatDate(request.created_at) }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 border-b border-gray-200 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="ml-2">
                    <div class="text-sm font-medium leading-5 text-gray-900">
                      {{ formatDate(request.updated_at) }}
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Mobile Cards View (visible on mobile) -->
    <div v-if="!loading && !error && videoRequests.length > 0" class="md:hidden space-y-4">
      <div
        v-for="(request, index) in videoRequests"
        :key="index"
        class="bg-white rounded-lg shadow-md border border-gray-200 p-4"
      >
        <!-- Card Header with Status -->
        <div class="flex justify-end items-start mb-3">
          <select v-if="isAdmin" :value="request.status" @change="handleStatusChange($event, request)" class="border border-gray-300 rounded px-2 py-1 text-xs">
            <option v-for="status in statuses" :value="status" :key="status">{{ getStatusLabel(status) }}</option>
          </select>
          <span v-else :class="getStatusClass(request.status)" class="inline-flex px-2 py-1 text-xs font-semibold rounded-full">
            {{ getStatusLabel(request.status) }}
          </span>
        </div>

        <!-- Title -->
        <div class="mb-3">
          <h3
            @click="openVideoModal(request)"
            class="text-lg font-semibold text-blue-600 hover:text-blue-800 cursor-pointer underline leading-tight"
          >
            {{ request.title }}
          </h3>
        </div>

        <!-- Description -->
        <div class="mb-4">
          <p class="text-sm text-gray-700 leading-relaxed">
            {{ request.description }}
          </p>
        </div>

        <!-- Dates -->
        <div class="space-y-2 pt-3 border-t border-gray-100">
          <div class="flex justify-between text-xs text-gray-500">
            <span>Fecha y Hora de Solicitud:</span>
            <span class="font-medium text-gray-900">{{ formatDate(request.created_at) }}</span>
          </div>
          <div class="flex justify-between text-xs text-gray-500">
            <span>Última Actualización:</span>
            <span class="font-medium text-gray-900">{{ formatDate(request.updated_at) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for New Video Request -->
    <div v-if="showNewRequestModal" class="fixed inset-0 flex items-center justify-center bg-gray-600 bg-opacity-50 z-50">
      <div class="bg-white rounded-lg overflow-hidden shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <!-- Modal Header -->
        <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 class="text-xl font-semibold text-gray-800">Nueva Solicitud de Video</h2>
          <button
            @click="closeNewRequestModal"
            class="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Modal Body with RequestVideo Component -->
        <div class="p-6">
          <RequestVideo
            ref="requestVideoComponent"
            @request-submitted="handleRequestSubmitted"
            @request-cancelled="closeNewRequestModal"
          />
        </div>
      </div>
    </div>

    <!-- Modal for YouTube Video -->
    <div v-if="showVideoModal" class="fixed inset-0 flex items-center justify-center bg-gray-600 bg-opacity-50 z-50">
      <div class="bg-white rounded-lg overflow-hidden shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <!-- Modal Header -->
        <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 class="text-xl font-semibold text-gray-800">{{ selectedVideoRequest?.title }}</h2>
          <button
            @click="closeVideoModal"
            class="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="p-6">
          <!-- Video Description -->
          <div class="mb-4">
            <p class="text-gray-700">{{ selectedVideoRequest?.description }}</p>
          </div>

          <!-- YouTube Video -->
          <div v-if="selectedVideoRequest?.youtube_url" class="mb-4">
            <h3 class="text-lg font-semibold mb-2">Video de YouTube:</h3>
            <div class="relative w-full" style="padding-bottom: 56.25%;">
              <iframe
                :src="getYouTubeEmbedUrl(selectedVideoRequest.youtube_url)"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                class="absolute top-0 left-0 w-full h-full rounded-lg"
              ></iframe>
            </div>
          </div>

          <!-- No Video Message -->
          <div v-else class="mb-4">
            <div class="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
              <p>No se proporcionó video de YouTube para esta solicitud.</p>
            </div>
          </div>

          <!-- Video Details -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span class="font-semibold text-gray-600">Estado:</span>
              <span :class="getStatusClass(selectedVideoRequest?.status)" class="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full">
                {{ getStatusLabel(selectedVideoRequest?.status) }}
              </span>
            </div>
            <div>
              <span class="font-semibold text-gray-600">Fecha de Solicitud:</span>
              <span class="ml-2">{{ formatDate(selectedVideoRequest?.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Status Update Modal -->
    <ConfirmAction
      :isVisible="showConfirmModal"
      :processing="confirmProcessing"
      title="Confirmar cambio de estado"
      action="Cambiar estado"
      :description="`¿Estás seguro de cambiar el estado de '${statusToUpdate?.title}' a '${getStatusLabel(statusToUpdate?.status)}'?`"
      @confirm="confirmStatusUpdate"
      @cancel="cancelStatusUpdate"
    />
  </div>
</template>

<script>
import RequestVideo from './RequestVideo.vue'
import ConfirmAction from '../components/commons/ConfirmAction.vue'

export default {
  name: "RequestedVideos",
  components: {
    RequestVideo,
    ConfirmAction
  },
  data() {
    return {
      videoRequests: [],
      loading: false,
      error: null,
      showNewRequestModal: false,
      showVideoModal: false,
      selectedVideoRequest: null,
      statuses: ['pending', 'downloaded', 'accepted', 'processing', 'published'],
      showConfirmModal: false,
      statusToUpdate: null,
      confirmProcessing: false
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.authentication.user.role === 'admin';
    }
  },
  created() {
    this.getVideoRequests();
  },
  methods: {
    async getVideoRequests() {
      this.loading = true;
      this.error = null;

      try {
        const endpoint = this.$store.state.authentication.user.role === 'admin' ? '/video-requests' : '/video-requests/my-requests';
        const response = await this.$http.get(endpoint);
        const data = response.data;
        this.videoRequests = data.data || [];
      } catch (error) {
        console.error('Error al cargar los videos solicitados:', error);
        this.error = error.response?.data?.message || 'Error al cargar los videos solicitados';
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    },
    getStatusLabel(status) {
      if (!status) return '-';
      const statusLabels = {
        'pending': 'Pendiente',
        'downloaded': 'Descargado',
        'accepted': 'Aceptado',
        'processing': 'Procesando',
        'published': 'Listo',
        'approved': 'Aprobado',
        'rejected': 'Rechazado',
        'in_progress': 'En progreso',
        'completed': 'Completado'
      };
      return statusLabels[status?.toLowerCase()] || status;
    },
    getStatusClass(status) {
      const statusClasses = {
        'pending': 'bg-yellow-100 text-yellow-800',
        'downloaded': 'bg-blue-100 text-blue-800',
        'accepted': 'bg-green-100 text-green-800',
        'processing': 'bg-purple-100 text-purple-800',
        'published': 'bg-green-100 text-green-800',
        'approved': 'bg-green-100 text-green-800',
        'rejected': 'bg-red-100 text-red-800',
        'in_progress': 'bg-blue-100 text-blue-800',
        'completed': 'bg-green-100 text-green-800'
      };
      return statusClasses[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    },
    openNewRequestModal() {
      this.showNewRequestModal = true;
    },
    closeNewRequestModal() {
      this.showNewRequestModal = false;
      // Reset the form in the RequestVideo component when modal closes
      if (this.$refs.requestVideoComponent) {
        this.$refs.requestVideoComponent.resetForm();
      }
    },
    handleRequestSubmitted() {
      // Close modal immediately after successful submission
      this.closeNewRequestModal();

      // Refresh the table data to show the new request
      this.getVideoRequests();
    },
    openVideoModal(request) {
      this.selectedVideoRequest = request;
      this.showVideoModal = true;
    },
    closeVideoModal() {
      this.showVideoModal = false;
      this.selectedVideoRequest = null;
    },
    getYouTubeEmbedUrl(url) {
      if (!url) return '';

      // If it's already an embed URL, return as is
      if (url.includes('youtube.com/embed/')) {
        return url;
      }

      let videoId = '';

      // Handle YouTube Shorts
      if (url.includes('youtube.com/shorts/')) {
        // Format: https://www.youtube.com/shorts/VIDEO_ID
        videoId = url.split('shorts/')[1].split('?')[0];
      }
      // Handle regular YouTube URLs
      else if (url.includes('youtu.be/')) {
        // Format: https://youtu.be/VIDEO_ID
        videoId = url.split('youtu.be/')[1].split('?')[0].split('&')[0];
      } else if (url.includes('youtube.com/watch?v=')) {
        // Format: https://www.youtube.com/watch?v=VIDEO_ID
        videoId = url.split('v=')[1].split('&')[0];
      } else {
        // Try regex as fallback
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|shorts\/|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        if (match && match[2].length === 11) {
          videoId = match[2];
        }
      }

      if (videoId && videoId.length === 11) {
        // For YouTube Shorts, use regular embed URL (it works the same)
        return `https://www.youtube.com/embed/${videoId}`;
      }

      return '';
    },
    handleStatusChange(event, request) {
      this.statusToUpdate = { id: request.id, status: event.target.value, title: request.title };
      this.showConfirmModal = true;
    },
    async confirmStatusUpdate() {
      this.confirmProcessing = true;
      try {
        await this.$http.put(`/video-requests/${this.statusToUpdate.id}/status`, { status: this.statusToUpdate.status });
        this.getVideoRequests();
      } catch (error) {
        console.error('Error updating status:', error);
        this.error = 'Error al actualizar el estado';
      } finally {
        this.confirmProcessing = false;
        this.showConfirmModal = false;
        this.statusToUpdate = null;
      }
    },
    cancelStatusUpdate() {
      this.showConfirmModal = false;
      this.statusToUpdate = null;
    }
  }
};
</script>

<style scoped>
/* Add specific styles if needed */
</style>

